import { useEffect, useRef } from "react";
import { ensureHistoryEventsPatched, HISTORY_EVENTS_GUARD_MARKER } from "@/lib/history-events-guard";

const LEAVE_WARNING = "You have unsaved changes to this message rule. Leave without saving?";
const HISTORY_INDEX = "__messageRuleHistoryIndex";

/** Guard Wouter's History API navigation before it can unmount the form. */
export function useUnsavedDraft(dirty: boolean) {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    ensureHistoryEventsPatched();
    const originalPush = history.pushState;
    const originalReplace = history.replaceState;
    let currentUrl = window.location.href;
    let currentState = history.state;
    let currentIndex = currentState?.[HISTORY_INDEX] ?? 0;
    let restoring = false;

    const confirmLeave = () => !dirtyRef.current || window.confirm(LEAVE_WARNING);
    const rememberLocation = () => {
      currentUrl = window.location.href;
      currentState = history.state;
    };
    // Index entries created while this form is mounted so back/forward can
    // restore the actual entry instead of replacing it and losing history.
    originalReplace.call(history, { ...currentState, [HISTORY_INDEX]: currentIndex }, "", currentUrl);
    rememberLocation();

    const wrap = (original: History["pushState"], push: boolean): History["pushState"] =>
      function (state, title, url) {
        const nextUrl = url == null ? currentUrl : new URL(url, currentUrl).href;
        if (nextUrl !== currentUrl && !confirmLeave()) return;
        if (push) currentIndex += 1;
        original.call(history, { ...state, [HISTORY_INDEX]: currentIndex }, title, url);
        rememberLocation();
      };
    const guardedPush = wrap(originalPush, true);
    const guardedReplace = wrap(originalReplace, false);
    Object.assign(guardedPush, { [HISTORY_EVENTS_GUARD_MARKER]: true });
    Object.assign(guardedReplace, { [HISTORY_EVENTS_GUARD_MARKER]: true });
    history.pushState = guardedPush;
    history.replaceState = guardedReplace;

    const onPopState = (event: PopStateEvent) => {
      if (restoring) {
        restoring = false;
        rememberLocation();
        return;
      }
      if (window.location.href === currentUrl) return;
      const nextIndex = event.state?.[HISTORY_INDEX];
      if (!confirmLeave()) {
        // Capture runs before Wouter's listeners: don't render the other page
        // while the browser returns to the entry the host chose to keep.
        event.stopImmediatePropagation();
        restoring = true;
        history.go(typeof nextIndex === "number" ? currentIndex - nextIndex : 1);
        return;
      }
      currentIndex = typeof nextIndex === "number" ? nextIndex : currentIndex - 1;
      rememberLocation();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("popstate", onPopState, true);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      if (history.pushState === guardedPush) history.pushState = originalPush;
      if (history.replaceState === guardedReplace) history.replaceState = originalReplace;
      window.removeEventListener("popstate", onPopState, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  // Saves need to clear the guard synchronously, before React commits again.
  return () => { dirtyRef.current = false; };
}
